import { useCallback, useContext, useEffect } from "react"
import { useSelector } from "react-redux"
import { getLoginUrl } from "../../authentication/helpers"
import { ConfigurationContext } from "../../context-providers/configuration-context"
import { AppState } from "../../store"
import { buildRedirectUrl, handleSwGPurchase } from "./subscriptions"

export const useSubscribeWithGoogle = (buttonRef: HTMLButtonElement, googleSKU: string) => {
    const { subscriptions, eventManagerLoaded } = useSelector(
        (state: AppState) => state.SwG,
    )
    const {
        isLoggedIn,
        userEmail,
        checkingTokenState,
        hasAttemptedValidation,
    } = useSelector((state: AppState) => state.authentication)

    const localConfig = useContext(ConfigurationContext)

    const handleSubscribeWithGoogleButton = useCallback(
        (sku?: string) => {
            if (subscriptions && sku) {
                // Setup callback for payment response, noting that this callback only gets triggered when the user successfully subscribes to the SKU
                subscriptions.setOnPaymentResponse((paymentResponse) => {
                    paymentResponse.then(async (response) => {
                        // At this point, we need to handle the userData as well as purchaseData from the user
                        // Cast responseObject to object.
                        const responseObject = await handleSwGPurchase(
                            localConfig,
                            response,
                        )
    
                        // Update the frontend prompt to tell that the user that their account has been created.
    
                        response.complete().then(() => {
                            // Once the user has acknowledged that their account has been created, we need to then redirect to the authSite to finish their login
                            if (responseObject) {
                                // Need to see if the user is logged in to the account they bought the SKU with
                                const isSameAccount =
                                    response.userData.email === userEmail
    
                                window.location.href =
                                    isLoggedIn && isSameAccount
                                        ? buildRedirectUrl(window.location)
                                        : getLoginUrl({
                                              prompt: 'google',
                                              login_hint: responseObject.sub,
                                          })
                            }
                        })
                    })
                })
    
                subscriptions?.subscribe(sku)
            }
        },
        [isLoggedIn, subscriptions, userEmail, localConfig],
    )
    
    // We want to attach the SwG Button listeners when the following conditions have been fulfilled:
    // 1. SwG.js is loaded (subscriptions)
    // 2. Button is rendered as an element
    // 3. Event Manager in swg.js is initialised
    // 4. The user's authentication status has been confirmed.
    
    useEffect(() => {
        subscriptions &&
            buttonRef !== null &&
            eventManagerLoaded &&
            !checkingTokenState &&
            hasAttemptedValidation &&
            subscriptions.attachButton(buttonRef, () =>
                handleSubscribeWithGoogleButton(googleSKU),
            )
    }, [
        subscriptions,
        googleSKU,
        handleSubscribeWithGoogleButton,
        eventManagerLoaded,
        checkingTokenState,
        hasAttemptedValidation,
        buttonRef
    ])
}



